// World state is the umbrella container handed to systems each tick.
// It bundles the ECS world, time, RNG, spatial hashes, and input
// (cursor position from the player) so systems take one parameter.

import { World } from './ecs';
import { makeRng, type Rng, deriveSeed } from './rng';
import { SpatialHash } from './spatial-hash';

export interface DeathEvent {
  x: number;
  y: number;
  faction: 'ally' | 'enemy';
}

export interface CursorInput {
  /** World-space cursor coords. */
  x: number;
  y: number;
}

export class WorldState {
  readonly world = new World();
  readonly rngs: Record<string, Rng>;
  readonly allyHash = new SpatialHash(80);
  readonly enemyHash = new SpatialHash(80);
  /** Total sim time elapsed, seconds. */
  time = 0;
  /** Latest cursor position from the player. */
  cursor: CursorInput = { x: 0, y: 0 };
  /** Player-controlled flock attractor active? (always true in v1; reserved) */
  cursorActive = true;
  /** Bounded world rectangle. */
  bounds = { minX: -800, minY: -450, maxX: 800, maxY: 450 };
  /** Counter of enemy kills for run stats. */
  killCount = 0;
  /** Number of breeds completed this run. */
  breedCount = 0;
  /** Total projectile-hit events this run (used to drive hit SFX). */
  hitCount = 0;
  /** Lifetime number of allies destroyed (death SFX driver). */
  allyDeathCount = 0;
  /** Master seed for this run. */
  readonly seed: number;
  /** Positions of entities that died this sim step. Renderer drains this. */
  readonly deathEvents: DeathEvent[] = [];

  constructor(seed: number) {
    this.seed = seed | 0;
    this.rngs = {
      flocking: makeRng(deriveSeed(this.seed, 'flocking')),
      combat: makeRng(deriveSeed(this.seed, 'combat')),
      spawn: makeRng(deriveSeed(this.seed, 'spawn')),
      breeding: makeRng(deriveSeed(this.seed, 'breeding')),
      pickups: makeRng(deriveSeed(this.seed, 'pickups')),
    };
  }

  /** Get an RNG for a named subsystem. */
  rng(name: keyof WorldState['rngs']): Rng {
    return this.rngs[name];
  }
}
